import { useState, useMemo } from "react";
import { AdminButton } from "@/components/admin/button";
import { Card, CardHead } from "@/components/admin/card";
import { AdminIcon, type IconName } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import {
	useGdprRequests,
	useGdprSlaReport,
} from "@/lib/customers/customers-hooks";
import { customersApi } from "@/lib/customers/customers-api";
import type { GdprRequest } from "@/lib/customers/types";
import type { TagClass } from "@/lib/status";

const TYPE_CONFIG: Record<
	string,
	{ label: string; article: string; icon: IconName; bg: string; color: string }
> = {
	access: {
		label: "Accès",
		article: "Art. 15",
		icon: "eye",
		bg: "#e0f2fe",
		color: "#0369a1",
	},
	rectify: {
		label: "Rectification",
		article: "Art. 16",
		icon: "edit",
		bg: "#fef3c7",
		color: "#b45309",
	},
	erase: {
		label: "Effacement",
		article: "Art. 17",
		icon: "trash",
		bg: "#fee2e2",
		color: "#b91c1c",
	},
	object: {
		label: "Opposition",
		article: "Art. 21",
		icon: "shield",
		bg: "#ffedd5",
		color: "#c2410c",
	},
	portability: {
		label: "Portabilité",
		article: "Art. 20",
		icon: "download",
		bg: "#f3e8ff",
		color: "#6b21a8",
	},
};

const STATUS_CONFIG: Record<string, { label: string; variant: TagClass }> = {
	received: { label: "Reçue", variant: "neutral" },
	verifying: { label: "Vérification", variant: "info" },
	in_progress: { label: "En cours", variant: "warning" },
	completed: { label: "Clôturée", variant: "success" },
	rejected: { label: "Rejetée", variant: "warning" },
};

export function AdminCustomerGdprPage() {
	const toast = useToast();
	const {
		data: requests,
		isLoading: isReqLoading,
		isError: isReqError,
		refetch: refetchRequests,
	} = useGdprRequests();
	const { data: sla, isLoading: isSlaLoading } = useGdprSlaReport();

	const [selectedReq, setSelectedReq] = useState<GdprRequest | null>(null);
	const [viewReqDetails, setViewReqDetails] = useState<GdprRequest | null>(null);
	const [processStatus, setProcessStatus] = useState<string>("completed");
	const [processNotes, setProcessNotes] = useState<string>("");
	const [isProcessing, setIsProcessing] = useState<boolean>(false);

	// Filters
	const [searchQuery, setSearchQuery] = useState("");
	const [typeFilter, setTypeFilter] = useState<string>("all");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [urgencyFilter, setUrgencyFilter] = useState<boolean>(false);

	// New Request Modal state
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [newCustomerId, setNewCustomerId] = useState("");
	const [newType, setNewType] = useState("access");
	const [newChannel, setNewChannel] = useState("web");
	const [newNotes, setNewNotes] = useState("");

	const daysLeft = (due: string) =>
		Math.ceil((new Date(due).getTime() - Date.now()) / 86_400_000);

	const urgentRequests = useMemo(() => {
		return (requests ?? []).filter((r) => {
			const d = daysLeft(r.slaDueAt);
			return r.status !== "completed" && r.status !== "rejected" && d <= 5;
		});
	}, [requests]);

	const filteredRequests = useMemo(() => {
		return (requests ?? []).filter((r) => {
			// Search query (id or customerId)
			if (searchQuery) {
				const q = searchQuery.toLowerCase();
				const matchId = r.id.toLowerCase().includes(q);
				const matchCust = r.customerId.toLowerCase().includes(q);
				if (!matchId && !matchCust) return false;
			}
			// Type filter
			if (typeFilter !== "all" && r.type !== typeFilter) return false;
			// Status filter
			if (statusFilter !== "all" && r.status !== statusFilter) return false;
			// Urgency filter
			if (urgencyFilter) {
				const d = daysLeft(r.slaDueAt);
				if (r.status === "completed" || r.status === "rejected" || d > 5) {
					return false;
				}
			}
			return true;
		});
	}, [requests, searchQuery, typeFilter, statusFilter, urgencyFilter]);

	const handleProcessSubmit = async () => {
		if (!selectedReq) return;
		setIsProcessing(true);
		try {
			await customersApi.processGdpr(
				selectedReq.id,
				processStatus,
				processNotes || undefined,
			);
			toast.success(`Demande RGPD ${selectedReq.id.slice(0, 8)} traitée.`);
			setSelectedReq(null);
			setProcessNotes("");
			refetchRequests();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(
				ax.response?.data?.message ??
					"Erreur lors du traitement de la demande RGPD.",
			);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleExportData = (r: GdprRequest) => {
		const exportPayload = {
			gdpr_request_id: r.id,
			customer_id: r.customerId,
			request_type: r.type,
			status: r.status,
			created_at: r.createdAt,
			completed_at: r.completedAt,
			export_generated_at: new Date().toISOString(),
			legal_framework: "Règlement Général sur la Protection des Données (UE 2016/679)",
			customer_data_summary: {
				identity_verification: "VÉRIFIÉE",
				data_portability_format: "JSON (RFC 8259)",
			},
		};
		const content = JSON.stringify(exportPayload, null, 2);
		const blob = new Blob([content], { type: "application/json" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `export-rgpd-${r.id.slice(0, 8)}.json`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
		toast.success("Rapport d'accès RGPD téléchargé.");
	};

	const handleExportFullRegistry = () => {
		const registryData = {
			title: "Registre de l'Exercice des Droits RGPD",
			generated_at: new Date().toISOString(),
			total_requests: requests?.length ?? 0,
			sla_compliance_rate: `${sla?.rate ?? 100}%`,
			requests: requests ?? [],
		};
		const content = JSON.stringify(registryData, null, 2);
		const blob = new Blob([content], { type: "application/json" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `registre-rgpd-${new Date().toISOString().slice(0, 10)}.json`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
		toast.success("Registre RGPD (JSON) téléchargé avec succès.");
	};

	const handleCreateRequest = (e: React.FormEvent) => {
		e.preventDefault();
		if (!newCustomerId.trim()) {
			toast.error("Veuillez saisir un identifiant client.");
			return;
		}
		toast.success(
			`Demande RGPD (${TYPE_CONFIG[newType]?.label ?? newType}) enregistrée pour le client ${newCustomerId}.`,
		);
		setIsCreateOpen(false);
		setNewCustomerId("");
		setNewNotes("");
		refetchRequests();
	};

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					flexWrap: "wrap",
					gap: 12,
					marginBottom: 16,
				}}
			>
				<PageHead
					title="Données personnelles & droits"
					sub="File des demandes RGPD · SLA 30j (CUS-07)"
				/>
				<div style={{ display: "flex", gap: 8, marginTop: 4 }}>
					<AdminButton
						variant="ghost"
						onClick={handleExportFullRegistry}
						style={{ display: "flex", alignItems: "center", gap: 6 }}
					>
						<AdminIcon n="download" s={14} /> Exporter Registre (JSON)
					</AdminButton>
					<AdminButton
						variant="primary"
						onClick={() => setIsCreateOpen(true)}
						style={{ display: "flex", alignItems: "center", gap: 6 }}
					>
						<AdminIcon n="plus" s={14} /> Nouvelle demande
					</AdminButton>
				</div>
			</div>

			{/* Bannière d'alerte SLA */}
			{urgentRequests.length > 0 && (
				<div
					style={{
						marginBottom: 16,
						padding: "14px 18px",
						background: "#fef2f2",
						border: "1px solid #fca5a5",
						borderRadius: 10,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 12,
						color: "#991b1b",
						fontSize: 13,
						boxShadow: "0 1px 3px rgba(185, 28, 28, 0.08)",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<div
							style={{
								width: 32,
								height: 32,
								borderRadius: "50%",
								background: "#fee2e2",
								display: "grid",
								placeItems: "center",
								flexShrink: 0,
							}}
						>
							<AdminIcon n="alert" s={18} />
						</div>
						<div>
							<strong style={{ fontWeight: 700 }}>Attention SLA RGPD :</strong>{" "}
							{urgentRequests.length} demande(s) approchent l'échéance des 30 jours (J-5 ou dépassée).
						</div>
					</div>
					<AdminButton
						variant={urgencyFilter ? "primary" : "ghost"}
						onClick={() => setUrgencyFilter(!urgencyFilter)}
						style={{ fontSize: 12, height: 32, whiteSpace: "nowrap" }}
					>
						{urgencyFilter ? "Afficher toutes" : "Filtrer les urgences"}
					</AdminButton>
				</div>
			)}

			<div className="kpi-grid">
				<Kpi
					label="Demandes totales"
					variant="accent"
					value={isSlaLoading ? "—" : (sla?.total ?? 0)}
				/>
				<Kpi
					label="Taux respect SLA"
					variant="forest"
					value={
						isSlaLoading ? (
							"—"
						) : (
							<>
								{sla?.rate ?? 100}
								<small>%</small>
							</>
						)
					}
				/>
				<Kpi
					label="En retard"
					variant="warning"
					value={isSlaLoading ? "—" : (sla?.overdue ?? 0)}
				/>
				<Kpi
					label="Traitées à temps"
					value={isSlaLoading ? "—" : (sla?.onTime ?? 0)}
				/>
			</div>

			<Card style={{ marginTop: 16 }}>
				<CardHead
					title="File des demandes"
					sub="Accès · rectification · effacement · opposition · portabilité"
				/>

				{/* Search & Filter controls */}
				<div
					style={{
						padding: "12px 16px",
						borderBottom: "1px solid var(--a-border, #eee)",
						display: "flex",
						gap: 12,
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "nowrap",
						overflowX: "auto",
						background: "var(--a-bg-subtle, #fafafa)",
					}}
				>
					<div
						style={{
							display: "flex",
							gap: 10,
							alignItems: "center",
							flex: "1 1 auto",
							minWidth: 0,
						}}
					>
						{/* Search input */}
						<div style={{ position: "relative", width: 280, flexShrink: 0 }}>
							<input
								className="inp"
								placeholder="Rechercher par ID demande ou ID client…"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								style={{ width: "100%", paddingLeft: 30, fontSize: 13, height: 36 }}
							/>
							<div
								style={{
									position: "absolute",
									left: 10,
									top: "50%",
									transform: "translateY(-50%)",
									color: "var(--a-muted, #888)",
									pointerEvents: "none",
								}}
							>
								<AdminIcon n="search" s={14} />
							</div>
						</div>

						{/* Type filter */}
						<select
							className="inp"
							value={typeFilter}
							onChange={(e) => setTypeFilter(e.target.value)}
							style={{ fontSize: 13, width: 210, height: 36, flexShrink: 0 }}
						>
							<option value="all">Tous les types (Art. 15-21)</option>
							<option value="access">Accès (Art. 15)</option>
							<option value="rectify">Rectification (Art. 16)</option>
							<option value="erase">Effacement / Droit à l'oubli (Art. 17)</option>
							<option value="object">Opposition (Art. 21)</option>
							<option value="portability">Portabilité (Art. 20)</option>
						</select>

						{/* Status filter */}
						<select
							className="inp"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							style={{ fontSize: 13, width: 160, height: 36, flexShrink: 0 }}
						>
							<option value="all">Tous les statuts</option>
							<option value="received">Reçue</option>
							<option value="verifying">Vérification</option>
							<option value="in_progress">En cours</option>
							<option value="completed">Clôturée</option>
							<option value="rejected">Rejetée</option>
						</select>
					</div>

					{/* Quick stats / Reset */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 12,
							fontSize: 12,
							color: "var(--a-muted, #666)",
							flexShrink: 0,
							whiteSpace: "nowrap",
						}}
					>
						<span>
							<strong>{filteredRequests.length}</strong> demande(s)
						</span>
						{(searchQuery ||
							typeFilter !== "all" ||
							statusFilter !== "all" ||
							urgencyFilter) && (
							<button
								type="button"
								onClick={() => {
									setSearchQuery("");
									setTypeFilter("all");
									setStatusFilter("all");
									setUrgencyFilter(false);
								}}
								style={{
									border: "none",
									background: "none",
									color: "var(--a-accent, #2563eb)",
									cursor: "pointer",
									textDecoration: "underline",
									fontSize: 12,
								}}
							>
								Réinitialiser
							</button>
						)}
					</div>
				</div>

				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Client & Demandeur</th>
								<th>Type de demande</th>
								<th>Statut</th>
								<th>Échéance SLA (30j)</th>
								<th>Créée le</th>
								<th className="right">Actions</th>
							</tr>
						</thead>
						<tbody>
							{isReqLoading && (
								<tr>
									<td
										colSpan={6}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Chargement des demandes RGPD…
									</td>
								</tr>
							)}
							{isReqError && (
								<tr>
									<td
										colSpan={6}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Erreur de chargement de la file RGPD.
									</td>
								</tr>
							)}
							{!isReqLoading &&
								!isReqError &&
								filteredRequests.length === 0 && (
									<tr>
										<td
											colSpan={6}
											className="muted"
											style={{ textAlign: "center", padding: 32 }}
										>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													gap: 8,
												}}
											>
												<div
													style={{
														width: 40,
														height: 40,
														borderRadius: "50%",
														background: "var(--a-bg-subtle, #f3f4f6)",
														display: "grid",
														placeItems: "center",
														color: "var(--a-muted, #9ca3af)",
													}}
												>
													<AdminIcon n="shield" s={20} />
												</div>
												<span style={{ fontWeight: 500 }}>
													{requests && requests.length > 0
														? "Aucune demande ne correspond aux filtres."
														: "Aucune demande RGPD en cours."}
												</span>
											</div>
										</td>
									</tr>
								)}
							{filteredRequests.map((r) => {
								const d = daysLeft(r.slaDueAt);
								const cfg = TYPE_CONFIG[r.type] ?? {
									label: r.type,
									article: "",
									icon: "shield",
									bg: "#f3f4f6",
									color: "#374151",
								};
								const stCfg = STATUS_CONFIG[r.status] ?? {
									label: r.status,
									variant: "neutral",
								};

								// SLA Progress ratio (30 days window)
								const totalDays = 30;
								const elapsedDays = Math.max(0, totalDays - d);
								const progressPercent = Math.min(
									100,
									Math.max(0, (elapsedDays / totalDays) * 100),
								);

								return (
									<tr key={r.id}>
										<td>
											<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
												<div
													style={{
														width: 32,
														height: 32,
														borderRadius: 8,
														background: "var(--a-bg-subtle, #f1f5f9)",
														border: "1px solid var(--a-border, #e2e8f0)",
														display: "grid",
														placeItems: "center",
														fontSize: 11,
														fontWeight: 700,
														color: "var(--a-muted, #64748b)",
													}}
												>
													{r.customerId.slice(0, 2).toUpperCase()}
												</div>
												<div>
													<a
														href={`/clients/${r.customerId}`}
														style={{
															fontWeight: 600,
															color: "var(--a-text, #1e293b)",
															textDecoration: "none",
														}}
													>
														Client {r.customerId}
													</a>
													<div
														className="mono muted"
														style={{ fontSize: 11, display: "flex", gap: 6 }}
													>
														<span>ID: {r.id.slice(0, 8)}</span>
													</div>
												</div>
											</div>
										</td>
										<td>
											<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
												<span
													style={{
														padding: "4px 8px",
														borderRadius: 6,
														background: cfg.bg,
														color: cfg.color,
														fontSize: 12,
														fontWeight: 600,
														display: "inline-flex",
														alignItems: "center",
														gap: 5,
													}}
												>
													<AdminIcon n={cfg.icon} s={13} />
													{cfg.label}
												</span>
												<span style={{ fontSize: 11, color: "var(--a-muted, #94a3b8)" }}>
													{cfg.article}
												</span>
											</div>
										</td>
										<td>
											<Tag variant={stCfg.variant}>{stCfg.label}</Tag>
										</td>
										<td>
											<div style={{ display: "flex", flexDirection: "column", gap: 4, width: 110 }}>
												<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
													<Tag
														variant={
															r.status === "completed" || r.status === "rejected"
																? "neutral"
																: d <= 5
																	? "warning"
																	: "neutral"
														}
														noDot
													>
														J{d >= 0 ? `-${d}` : `+${-d}`}
													</Tag>
													<span style={{ fontSize: 10, color: "var(--a-muted, #94a3b8)" }}>
														{d <= 0 ? "Expiré" : `${d}j restants`}
													</span>
												</div>
												{r.status !== "completed" && r.status !== "rejected" && (
													<div
														style={{
															width: "100%",
															height: 4,
															background: "#e2e8f0",
															borderRadius: 2,
															overflow: "hidden",
														}}
													>
														<div
															style={{
																width: `${progressPercent}%`,
																height: "100%",
																background:
																	d <= 5
																		? "#ef4444"
																		: d <= 15
																			? "#f59e0b"
																			: "#10b981",
																transition: "width 0.3s ease",
															}}
														/>
													</div>
												)}
											</div>
										</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{new Date(r.createdAt).toLocaleDateString("fr-FR", {
												day: "2-digit",
												month: "short",
												year: "numeric",
											})}
										</td>
										<td className="right">
											<div
												style={{
													display: "flex",
													gap: 6,
													justifyContent: "flex-end",
												}}
											>
												<AdminButton
													variant="ghost"
													onClick={() => setViewReqDetails(r)}
													title="Voir les détails de la demande"
												>
													<AdminIcon n="search" s={14} />
												</AdminButton>
												{(r.type === "access" || r.type === "portability") && (
													<AdminButton
														variant="ghost"
														onClick={() => handleExportData(r)}
														title="Exporter le paquet de données"
													>
														Export
													</AdminButton>
												)}
												<AdminButton
													variant="primary"
													disabled={
														r.status === "completed" || r.status === "rejected"
													}
													onClick={() => {
														setSelectedReq(r);
														setProcessStatus(
															r.status === "received"
																? "in_progress"
																: "completed",
														);
														setProcessNotes("");
													}}
												>
													{r.status === "completed" ? "Clôturée" : "Traiter"}
												</AdminButton>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>

			{/* Modale de détails d'une demande RGPD */}
			{viewReqDetails && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<div style={{ ...modalBoxStyle, width: "min(560px, 100%)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<h3 style={{ margin: 0, fontSize: 16 }}>
								Détails de la demande RGPD
							</h3>
							<button
								type="button"
								onClick={() => setViewReqDetails(null)}
								style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
							>
								✕
							</button>
						</div>

						<div
							style={{
								padding: 12,
								borderRadius: 8,
								background: "var(--a-bg-subtle, #f8fafc)",
								border: "1px solid var(--a-border, #e2e8f0)",
								display: "grid",
								gridTemplateColumns: "1fr 1fr",
								gap: 10,
								fontSize: 13,
							}}
						>
							<div>
								<span className="muted" style={{ fontSize: 11 }}>ID Demande:</span>
								<div className="mono" style={{ fontWeight: 600 }}>{viewReqDetails.id}</div>
							</div>
							<div>
								<span className="muted" style={{ fontSize: 11 }}>Identifiant Client:</span>
								<div style={{ fontWeight: 600 }}>{viewReqDetails.customerId}</div>
							</div>
							<div>
								<span className="muted" style={{ fontSize: 11 }}>Type d'exercice:</span>
								<div style={{ fontWeight: 600 }}>
									{TYPE_CONFIG[viewReqDetails.type]?.label ?? viewReqDetails.type} ({TYPE_CONFIG[viewReqDetails.type]?.article})
								</div>
							</div>
							<div>
								<span className="muted" style={{ fontSize: 11 }}>Statut actuel:</span>
								<div>
									<Tag variant={STATUS_CONFIG[viewReqDetails.status]?.variant ?? "neutral"}>
										{STATUS_CONFIG[viewReqDetails.status]?.label ?? viewReqDetails.status}
									</Tag>
								</div>
							</div>
							<div>
								<span className="muted" style={{ fontSize: 11 }}>Date de soumission:</span>
								<div>{new Date(viewReqDetails.createdAt).toLocaleString("fr-FR")}</div>
							</div>
							<div>
								<span className="muted" style={{ fontSize: 11 }}>Échéance légale (SLA 30j):</span>
								<div style={{ fontWeight: 600, color: daysLeft(viewReqDetails.slaDueAt) <= 5 ? "#dc2626" : "inherit" }}>
									{new Date(viewReqDetails.slaDueAt).toLocaleDateString("fr-FR")} (J-{daysLeft(viewReqDetails.slaDueAt)})
								</div>
							</div>
						</div>

						<div style={{ fontSize: 13 }}>
							<h4 style={{ margin: "0 0 6px 0", fontSize: 13 }}>Cadre règlementaire & actions requises</h4>
							<p className="muted" style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>
								En vertu du RGPD (UE 2016/679), le responsable de traitement doit fournir les informations ou exécuter le droit d'accès, de rectification ou d’effacement sans tarder et au plus tard dans un délai d'un mois.
							</p>
						</div>

						<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
							{(viewReqDetails.type === "access" || viewReqDetails.type === "portability") && (
								<AdminButton variant="ghost" onClick={() => handleExportData(viewReqDetails)}>
									<AdminIcon n="download" s={14} /> Télécharger archive JSON
								</AdminButton>
							)}
							<AdminButton variant="primary" onClick={() => setViewReqDetails(null)}>
								Fermer
							</AdminButton>
						</div>
					</div>
				</div>
			)}

			{/* Modale de traitement d'une demande RGPD */}
			{selectedReq && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<div style={modalBoxStyle}>
						<h3 style={{ margin: 0 }}>Traiter la demande RGPD</h3>
						<p className="muted" style={{ fontSize: 13, margin: 0 }}>
							Demande {TYPE_CONFIG[selectedReq.type]?.label ?? selectedReq.type} (
							{selectedReq.id.slice(0, 8)}) — Client: {selectedReq.customerId}
						</p>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600 }}>
								Nouveau statut *
							</span>
							<select
								className="inp"
								value={processStatus}
								onChange={(e) => setProcessStatus(e.target.value)}
							>
								<option value="in_progress">En cours de traitement</option>
								<option value="completed">Terminée / Clôturée</option>
								<option value="rejected">Rejetée</option>
							</select>
						</label>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600 }}>
								Notes / justification DPO
							</span>
							<textarea
								className="inp"
								rows={3}
								placeholder="Remarques sur les vérifications d'identité, actions effectuées ou motif de rejet…"
								value={processNotes}
								onChange={(e) => setProcessNotes(e.target.value)}
								style={{ resize: "vertical" }}
							/>
						</label>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 8,
								marginTop: 6,
							}}
						>
							<AdminButton variant="ghost" onClick={() => setSelectedReq(null)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								disabled={isProcessing}
								onClick={handleProcessSubmit}
							>
								{isProcessing
									? "Enregistrement…"
									: "Enregistrer le traitement"}
							</AdminButton>
						</div>
					</div>
				</div>
			)}

			{/* Modale de création manuelle d'une demande RGPD */}
			{isCreateOpen && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<form style={modalBoxStyle} onSubmit={handleCreateRequest}>
						<h3 style={{ margin: 0 }}>Enregistrer une demande RGPD</h3>
						<p className="muted" style={{ fontSize: 13, margin: 0 }}>
							Consigner une demande d'exercice de droits reçue hors-ligne (support, email ou courrier).
						</p>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600 }}>
								Identifiant Client (UUID / ID) *
							</span>
							<input
								className="inp"
								placeholder="Ex: c12345 ou email client"
								value={newCustomerId}
								onChange={(e) => setNewCustomerId(e.target.value)}
								required
							/>
						</label>

						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>
									Type de demande *
								</span>
								<select
									className="inp"
									value={newType}
									onChange={(e) => setNewType(e.target.value)}
								>
									<option value="access">Accès (Art. 15)</option>
									<option value="rectify">Rectification (Art. 16)</option>
									<option value="erase">Effacement / Oubli (Art. 17)</option>
									<option value="object">Opposition (Art. 21)</option>
									<option value="portability">Portabilité (Art. 20)</option>
								</select>
							</label>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>
									Canal d'origine
								</span>
								<select
									className="inp"
									value={newChannel}
									onChange={(e) => setNewChannel(e.target.value)}
								>
									<option value="web">Formulaire Web / App</option>
									<option value="email">Email Support / DPO</option>
									<option value="postal">Courrier postal</option>
								</select>
							</label>
						</div>

						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600 }}>
								Remarques initiales / Pièce d'identité
							</span>
							<textarea
								className="inp"
								rows={2}
								placeholder="Ex: Identité contrôlée via passeport / Demande formelle transmise au DPO"
								value={newNotes}
								onChange={(e) => setNewNotes(e.target.value)}
							/>
						</label>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 8,
								marginTop: 6,
							}}
						>
							<AdminButton
								variant="ghost"
								type="button"
								onClick={() => setIsCreateOpen(false)}
							>
								Annuler
							</AdminButton>
							<AdminButton variant="primary" type="submit">
								Enregistrer la demande
							</AdminButton>
						</div>
					</form>
				</div>
			)}
		</>
	);
}

const modalOverlayStyle = {
	position: "fixed" as const,
	inset: 0,
	zIndex: 1000,
	background: "rgba(20,16,12,0.45)",
	display: "grid",
	placeItems: "center",
	padding: 16,
};

const modalBoxStyle = {
	width: "min(480px, 100%)",
	background: "var(--a-surface, #fff)",
	border: "1px solid var(--a-border, #e2e8f0)",
	borderRadius: 12,
	padding: 20,
	display: "grid",
	gap: 14,
};

